import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { jwtDecode } from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {

    constructor(
        private authService: AuthService,
        private router: Router
    ) {
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree | Observable<boolean | UrlTree> {

        const token = this.authService.getToken();

        if (!token) {
            return this.tryRefresh(route);
        }

        try {
            const decodedToken: any = jwtDecode(token);

            const isExpired = decodedToken.exp * 1000 < Date.now();

            if (isExpired) {
                return this.tryRefresh(route);
            }

            // 👉 Verifica se a rota tem restrição de roles
            const expectedRoles = (route.data['roles'] as string[] | []).map(
                (role: string) => role.toUpperCase()
            );

            if (expectedRoles && expectedRoles.length > 0) {
                // 🔀 Verifica as roles do localStorage ou do token
                const userRoles = JSON.parse(
                    localStorage.getItem('roles') || '[]'
                );

                // 📍 Validação extra: confere se roles do localStorage batem com o token
                const tokenRoles = decodedToken.roles || [];
                const validRoles = userRoles.every(
                    (role: string) => tokenRoles.includes(role)
                );

                const hasRole = expectedRoles.some(
                    role => userRoles.includes(role)
                );

                if (!hasRole || !validRoles) {
                    this.router.navigate(['/public/access']);
                    return false;
                }
            }

            return true;

        } catch (error) {
            return this.tryRefresh(route, 'Invalid token');
        }
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree | Observable<boolean | UrlTree> {

        return this.canActivate(childRoute, state);
    }

    /**
     * Tenta renovar o access token antes de redirecionar
     * o usuário para o login.
     */
    private tryRefresh(
        route: ActivatedRouteSnapshot,
        reason: string = 'Session expired'
    ): Observable<boolean | UrlTree> {

        console.log('[AuthGuard] Tentando renovar a sessão...');

        return this.authService.refresh().pipe(

            map((response) => {

                console.log('[AuthGuard] Sessão renovada com sucesso.');

                try {
                    const newToken = response.accessToken;
                    const decodedToken: any = jwtDecode(newToken);

                    /*
                     * O refresh foi realizado.
                     *
                     * Agora executamos novamente a validação
                     * das roles usando o novo JWT.
                     */
                    const expectedRoles =
                        (route.data['roles'] as string[] | []).map(
                            (role: string) => role.toUpperCase()
                        );

                    if (expectedRoles && expectedRoles.length > 0) {

                        const userRoles = JSON.parse(
                            localStorage.getItem('roles') || '[]'
                        );

                        const tokenRoles = decodedToken.roles || [];

                        const validRoles = userRoles.every(
                            (role: string) =>
                                tokenRoles.includes(role)
                        );

                        const hasRole = expectedRoles.some(
                            role => userRoles.includes(role)
                        );

                        if (!hasRole || !validRoles) {
                            this.router.navigate(['/public/access']);
                            return false;
                        }
                    }

                    return true;

                } catch {
                    this.handleUnauthenticated('Invalid refreshed token');
                    return false;
                }
            }),

            catchError((error) => {

                console.warn(
                    '[AuthGuard] Não foi possível renovar a sessão.',
                    error
                );

                this.handleUnauthenticated(reason);

                return of(false);
            })
        );
    }

    private handleUnauthenticated(
        reason: string = 'Unauthorized'
    ) {
        console.warn(`Access denied: ${reason}`);
        this.router.navigate(['/public/login']);
    }
}